import React from "react";
import moment from "moment";
import DataTable from "react-data-table-component";
import ReactPaginate from "react-paginate";
import { Modal } from "react-bootstrap";
import PageSizeSelect from "components/PageSizeSelect/PageSizeSelect";
import { formatDate, formatRupiah, sumAmounts } from "./pencairanFormat";
import "./PencairanMonitoring.css";

export default function PencairanMonitoring({
  rows, pending, error, search, appliedSearch, onSearch, onApply, onReset, onRefresh,
  page, pageCount, pageSize, pageSizeOptions, onPageChange, onPageSizeChange,
  detail, onDetail, onCloseDetail,
}) {
  const firstRow = page * pageSize + 1;
  const permohonan = sumAmounts(rows, (row) => row.stepEmpat?.nominalPermohonan);
  const pencairan = sumAmounts(rows, (row) => row.nominalPencairan);
  const branches = new Set(rows.map((row) => row.lokasiPencairan?._id || row.lokasiPencairan?.namaCabang).filter(Boolean));
  const amountNote = (amount) => amount.available === rows.length
    ? `${amount.available} data di halaman ini`
    : `${amount.available} data bernominal · ${rows.length - amount.available} belum tersedia`;
  const summary = [
    { label: "Data ditampilkan", value: rows.length, note: "Pengajuan berstatus disetujui", icon: "file-invoice", tone: "blue" },
    { label: "Nominal permohonan", value: formatRupiah(permohonan.total), note: amountNote(permohonan), icon: "wallet", tone: "blue" },
    { label: "Nominal pencairan", value: formatRupiah(pencairan.total), note: amountNote(pencairan), icon: "money-bill-wave", tone: "green" },
    { label: "Cabang realisasi", value: branches.size, note: "Cabang tercatat di halaman ini", icon: "landmark", tone: "amber" },
  ];
  const columns = [
    { id: "no", name: "No", width: "64px", center: true, selector: (row, index) => firstRow + index },
    { id: "nasabah", name: "Nasabah", width: "240px", wrap: true, style: { fontWeight: 600, color: "#0f172a" }, selector: (row) => row.namaLengkap || "-" },
    { id: "nik", name: "NIK / KTP", width: "184px", center: true, style: { fontVariantNumeric: "tabular-nums" }, selector: (row) => row.nomorIdentitas || "-" },
    { id: "referensi", name: "Referensi ID", minWidth: "240px", wrap: true, selector: (row) => row.id_pinjaman || "-" },
    { id: "permohonan", name: "Nominal Permohonan", minWidth: "220px", right: true, style: { fontVariantNumeric: "tabular-nums", fontWeight: 600 }, selector: (row) => formatRupiah(row.stepEmpat?.nominalPermohonan) },
    { id: "pencairan", name: "Nominal Pencairan", minWidth: "220px", right: true, style: { fontVariantNumeric: "tabular-nums", fontWeight: 700, color: "#047857" }, selector: (row) => formatRupiah(row.nominalPencairan) },
    { name: "Cabang Realisasi", minWidth: "220px", wrap: true, selector: (row) => row.lokasiPencairan?.namaCabang || "-" },
    { name: "No Akad", minWidth: "200px", wrap: true, selector: (row) => row.noAkad || "-" },
    {
      name: "Tanggal Akad", minWidth: "180px", center: true, sortable: true,
      selector: (row) => row.tglAkad && moment(row.tglAkad).isValid() ? moment(row.tglAkad).valueOf() : 0,
      format: (row) => formatDate(row.tglAkad),
    },
    { name: "No Agenda", minWidth: "200px", wrap: true, selector: (row) => row.noAgenda || "-" },
    { name: "Tanggal Agenda", minWidth: "180px", center: true, selector: (row) => formatDate(row.tglAgenda) },
    {
      id: "aksi", name: "Aksi", width: "128px", center: true,
      cell: (row) => (
        <button type="button" className="pencairan-detail-button" onClick={() => onDetail(row)}
          aria-label={`Detail pencairan ${row.namaLengkap || row.id_pinjaman}`}>
          <i className="fas fa-info-circle" aria-hidden="true" /> Detail
        </button>
      ),
    },
  ];
  const dataColumns = columns.filter((column) => !["no", "nasabah", "aksi"].includes(column.id));
  const customStyles = {
    table: { style: { minWidth: `${columns.reduce((total, column) => total + parseInt(column.width || column.minWidth, 10), 0)}px` } },
    head: { style: { zIndex: 5 } },
    headRow: { style: { minHeight: "56px" } },
    headCells: { style: { color: "#ffffff", fontWeight: 700, fontSize: "12px", padding: "12px 16px" } },
    rows: { style: { minHeight: "72px" } },
    cells: { style: { color: "#334155", fontSize: "13px", padding: "14px 16px", lineHeight: "1.6" } },
  };

  return (
    <section className="pencairan-monitor" aria-label="Monitoring pencairan">
      <header className="pencairan-hero">
        <div>
          <span className="pencairan-eyebrow">MONITORING KREDIT</span>
          <h1>Pencairan</h1>
          <p>Pantau nominal, cabang realisasi, serta informasi akad dan agenda nasabah.</p>
          <span className="pencairan-status-tag"><i className="fas fa-check-circle" aria-hidden="true" /> Pengajuan disetujui</span>
        </div>
        <button type="button" className="pencairan-refresh" onClick={onRefresh} disabled={pending}>
          <i className="fas fa-sync-alt" aria-hidden="true" /> Muat ulang
        </button>
      </header>

      <div className="pencairan-summary-caption"><h2>Ringkasan halaman ini</h2><span>Nominal dihitung dari data pada halaman yang sedang ditampilkan</span></div>
      <div className="pencairan-summary-grid" aria-busy={pending}>
        {summary.map((item) => (
          <div className={`pencairan-summary-item pencairan-tone-${item.tone}`} key={item.label}>
            <div className="pencairan-summary-label"><span>{item.label}</span><i className={`fas fa-${item.icon}`} aria-hidden="true" /></div>
            <strong>{pending || error ? "—" : item.value}</strong>
            <small>{pending ? "Memuat data…" : error ? "Data belum tersedia" : item.note}</small>
          </div>
        ))}
      </div>

      <form className="pencairan-filters" onSubmit={(event) => { event.preventDefault(); onApply(); }}>
        <div className="pencairan-search-field">
          <label htmlFor="pencairan-search">Cari pencairan</label>
          <div className="pencairan-search-input"><i className="fas fa-search" aria-hidden="true" />
            <input id="pencairan-search" type="search" value={search} onChange={onSearch} placeholder="Referensi ID atau nama nasabah" disabled={pending} />
          </div>
        </div>
        <div className="pencairan-filter-actions">
          <button type="submit" className="pencairan-primary-button" disabled={pending}>Cari pencairan</button>
          <button type="button" className="pencairan-secondary-button" onClick={onReset} disabled={pending}>Reset</button>
        </div>
      </form>

      <div className="pencairan-results" aria-busy={pending}>
        <div className="pencairan-results-heading">
          <div><h2>Daftar pencairan</h2><span className="pencairan-search-caption">{appliedSearch ? `Pencarian: ${appliedSearch}` : "Seluruh hasil sesuai akses Anda"}</span></div>
          <span className="pencairan-scroll-hint"><i className="fas fa-arrows-alt-h" aria-hidden="true" /> Geser untuk melihat kolom lainnya</span>
        </div>
        {pending ? (
          <div className="pencairan-loading" role="status"><span>Memuat data pencairan…</span>
            {[0, 1, 2, 3].map((index) => <div className="pencairan-skeleton" key={index} aria-hidden="true" />)}
          </div>
        ) : error ? (
          <div className="pencairan-empty" role="alert"><i className="fas fa-exclamation-circle" aria-hidden="true" />
            <h3>Data belum tersedia</h3><p>{error}</p><button type="button" className="pencairan-primary-button" onClick={onRefresh}>Coba lagi</button>
          </div>
        ) : !rows.length ? (
          <div className="pencairan-empty" role="status"><i className="fas fa-search" aria-hidden="true" />
            <h3>{appliedSearch ? "Pencairan tidak ditemukan" : "Belum ada data pencairan"}</h3>
            <p>{appliedSearch ? "Coba referensi atau nama nasabah yang berbeda." : "Data pencairan akan tampil di sini saat tersedia."}</p>
            {appliedSearch && <button type="button" className="pencairan-secondary-button" onClick={onReset}>Reset pencarian</button>}
          </div>
        ) : (
          <>
            <div className="pencairan-table-frame"><DataTable className="pencairan-data-table" keyField="id_pinjaman" data={rows} columns={columns} customStyles={customStyles} fixedHeader fixedHeaderScrollHeight="65vh" /></div>
            <div className="pencairan-mobile-list" aria-label="Daftar pencairan">
              {rows.map((row, index) => (
                <article className="pencairan-mobile-card" key={row.id_pinjaman}>
                  <header><span>{firstRow + index}</span><h3>{row.namaLengkap || "-"}</h3></header>
                  <dl>{dataColumns.map((column) => (
                    <div key={column.name} className={column.id === "pencairan" ? "pencairan-value-highlight" : undefined}>
                      <dt>{column.name}</dt><dd>{column.format ? column.format(row) : column.selector(row)}</dd>
                    </div>
                  ))}</dl>
                  <footer><span>Informasi akad dan realisasi</span>{columns[columns.length - 1].cell(row)}</footer>
                </article>
              ))}
            </div>
          </>
        )}
        <footer className="pencairan-table-footer">
          <div className="pencairan-page-size"><span>Baris per halaman</span><div>
            <PageSizeSelect value={pageSize} options={pageSizeOptions} onChange={onPageSizeChange} isDisabled={pending} />
          </div></div>
          {!pending && !error && <span className="pencairan-page-info">{rows.length ? `${firstRow}–${firstRow + rows.length - 1} · Halaman ${page + 1} dari ${Math.max(1, pageCount)}` : "0 pencairan"}</span>}
          {!pending && !error && pageCount > 0 && (
            <nav aria-label="Halaman pencairan"><ReactPaginate
              forcePage={page} pageCount={pageCount} pageRangeDisplayed={3} marginPagesDisplayed={1}
              containerClassName="pencairan-pagination" pageClassName="pencairan-page-item" pageLinkClassName="pencairan-page-link"
              previousClassName="pencairan-page-item" previousLinkClassName="pencairan-page-link" nextClassName="pencairan-page-item" nextLinkClassName="pencairan-page-link"
              breakClassName="pencairan-page-item" breakLinkClassName="pencairan-page-link" activeClassName="pencairan-page-active" activeLinkClassName="pencairan-page-current"
              disabledClassName="pencairan-page-disabled" previousLabel="‹" nextLabel="›" breakLabel="…"
              previousAriaLabel="Halaman sebelumnya" nextAriaLabel="Halaman berikutnya" ariaLabelBuilder={(number) => `Halaman ${number}`}
              disableInitialCallback onPageChange={({ selected }) => onPageChange(selected)}
            /></nav>
          )}
        </footer>
      </div>

      {detail && (
        <Modal show onHide={onCloseDetail} centered scrollable size="lg" className="pencairan-detail-modal" aria-labelledby="pencairan-detail-title">
          <Modal.Header closeButton><Modal.Title id="pencairan-detail-title">Detail pencairan</Modal.Title></Modal.Header>
          <Modal.Body>
            <div className="pencairan-detail-identity"><span>NASABAH</span><h3>{detail.namaLengkap || "-"}</h3></div>
            <dl className="pencairan-detail-grid">
              <div><dt>Nomor Register</dt><dd>{detail.regisNumber || "-"}</dd></div>
              {dataColumns.map((column) => (
                <div key={column.name} className={column.id === "pencairan" ? "pencairan-value-highlight" : undefined}>
                  <dt>{column.name}</dt><dd>{column.format ? column.format(detail) : column.selector(detail)}</dd>
                </div>
              ))}
            </dl>
          </Modal.Body>
          <Modal.Footer><button type="button" className="pencairan-secondary-button" onClick={onCloseDetail}>Tutup detail</button></Modal.Footer>
        </Modal>
      )}
    </section>
  );
}
